import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { IMAGES } from "@/assets/images";
import type { Service, Project, Testimonial } from "@/lib/index";

const springPresets = {
  snappy: { type: "spring" as const, stiffness: 400, damping: 30 },
  gentle: { type: "spring" as const, stiffness: 300, damping: 35 },
};

const hoverLift = {
  rest: { y: 0, scale: 1 },
  hover: { y: -4, scale: 1.02, transition: springPresets.snappy },
};

interface ServiceCardProps {
  service: Service;
}

export function ServiceCard({ service }: ServiceCardProps) {
  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="h-full"
    >
      <Card className="h-full overflow-hidden border-border/50 bg-gradient-to-br from-card via-card to-card/95 shadow-[0_8px_30px_-6px_color-mix(in_srgb,var(--primary)_15%,transparent)] hover:shadow-[0_12px_40px_-8px_color-mix(in_srgb,var(--primary)_25%,transparent)] transition-shadow duration-300">
        <div className="relative h-56 overflow-hidden">
          <motion.img
            src={IMAGES[service.imageKey as keyof typeof IMAGES]}
            alt={service.title}
            className="w-full h-full object-cover"
            whileHover={{ scale: 1.05 }}
            transition={springPresets.gentle}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-primary/20 backdrop-blur-sm border border-primary/30 flex items-center justify-center text-2xl">
                {service.icon}
              </div>
              <h3 className="text-xl font-semibold text-white drop-shadow-lg">
                {service.title}
              </h3>
            </div>
          </div>
        </div>
        <CardHeader className="pb-3">
          <CardDescription className="text-base leading-relaxed">
            {service.description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {service.features.map((feature, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ ...springPresets.snappy, delay: index * 0.05 }}
                className="flex items-start gap-2 text-sm text-muted-foreground"
              >
                <span className="text-primary mt-0.5">✦</span>
                <span>{feature}</span>
              </motion.li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </motion.div>
  );
}

interface ProjectCardProps {
  project: Project;
}

export function ProjectCard({ project }: ProjectCardProps) {
  const categoryLabels = {
    residential: 'Résidentiel',
    commercial: 'Commercial',
    consultation: 'Consultation',
    furniture: 'Ameublement',
  };

  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="h-full"
    >
      <Card className="h-full overflow-hidden border-border/50 bg-gradient-to-br from-card via-card to-card/95 shadow-[0_8px_30px_-6px_color-mix(in_srgb,var(--primary)_15%,transparent)] hover:shadow-[0_12px_40px_-8px_color-mix(in_srgb,var(--primary)_25%,transparent)] transition-shadow duration-300">
        <div className="relative h-64 overflow-hidden group">
          <motion.img
            src={IMAGES[project.imageKey as keyof typeof IMAGES]}
            alt={project.title}
            className="w-full h-full object-cover"
            whileHover={{ scale: 1.08 }}
            transition={springPresets.gentle}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent opacity-90 group-hover:opacity-95 transition-opacity duration-300" />
          <div className="absolute top-4 right-4">
            <Badge className="bg-primary/90 text-primary-foreground border-primary/30 backdrop-blur-sm">
              {categoryLabels[project.category]}
            </Badge>
          </div>
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-end justify-between">
              <div>
                <h3 className="text-2xl font-semibold text-white drop-shadow-lg mb-1">
                  {project.title}
                </h3>
                <div className="flex items-center gap-3 text-sm text-white/80">
                  <span>{project.year}</span>
                  {project.location && (
                    <>
                      <span>•</span>
                      <span>{project.location}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <CardContent className="pt-6">
          <p className="text-muted-foreground leading-relaxed">
            {project.description}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}

interface TestimonialCardProps {
  testimonial: Testimonial;
}

export function TestimonialCard({ testimonial }: TestimonialCardProps) {
  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="h-full"
    >
      <Card className="h-full border-border/50 bg-gradient-to-br from-card via-card to-card/95 shadow-[0_8px_30px_-6px_color-mix(in_srgb,var(--primary)_15%,transparent)] hover:shadow-[0_12px_40px_-8px_color-mix(in_srgb,var(--primary)_25%,transparent)] transition-shadow duration-300">
        <CardHeader>
          <div className="flex items-start justify-between mb-4">
            <div>
              <CardTitle className="text-xl mb-1">{testimonial.clientName}</CardTitle>
              <CardDescription className="text-sm">
                {testimonial.projectType}
              </CardDescription>
            </div>
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${
                    i < testimonial.rating
                      ? 'fill-primary text-primary'
                      : 'fill-muted text-muted'
                  }`}
                />
              ))}
            </div>
          </div>
          <div className="relative">
            <span className="absolute -top-2 -left-1 text-6xl text-primary/20 font-serif leading-none">
              "
            </span>
            <blockquote className="text-lg leading-relaxed pl-6 italic">
              {testimonial.content}
            </blockquote>
          </div>
        </CardHeader>
      </Card>
    </motion.div>
  );
}